<script lang="ts">
	// see https://github.com/sveltejs/kit/pull/6197#issue-1348175366
	// export const csr = false;
	// export const prerender = true;
	// export const prerender = 'auto';
	// export const csr = true;

	import '../global.postcss';
	import '../app.css';
	import { onMount } from 'svelte';
	// import Loader from '$lib/components/common/Loader.svelte';
	import ListSignup from '$lib/components/hero/listSignup.svelte';
	import Sidebar from '$lib/components/common/Sidebar.svelte';
	import Footer from '$lib/components/footer/footer.svelte';
	import { browser } from '$app/environment';
	// , dev
	import { loadPlacenames } from '$lib/locations/placenames.store';
	// import { beforeNavigate } from '$app/navigation';
	import { navigating } from '$app/stores';
	import PageNavIndicator from '$lib/components/common/PageNavIndicator.svelte';
	import AlertList from '$lib/components/alert/AlertList.svelte';
	import {
		profileStore,
		user,
		profileLoading,
		isLoggingOut
		// history
	} from '$lib/user';
	import { get } from 'svelte/store';
	import { setSupabaseAuthJWT, restoreSupabaseJWT, supabaseClient } from '$lib/supabase';
	import { width } from '$lib/store';
	import { decodeJWTPayload, isJWTExpired } from '$lib/utils/jwtUtils.client';
	import { hydrated } from '$lib/user/hydrated.store';
	import { getProfile } from '$lib/user/profile/profile.api';
	import ParaModalWrapper from '$lib/components/para/ParaModalWrapper.svelte';

	/** @type {import('./$types').PageData} */
	export let data;
	// let userHasSidebar = false;
	// ParaModal is now loaded dynamically when needed

	// Navbar component loading
	let NavbarComponent: any = null;

	// Set user from server data if available
	// This ensures server-side auth data is used
	if (data.user && data.user.id) {
		user.set(data.user);
	}

	// Track sidebar visibility - initialize based on server data but update reactively
	let showSidebar = false;

	// Initialize sidebar visibility - show for logged-in members
	// Use server data as primary source, fallback to client data if server data not available
	$: showSidebar = !!(
		// Primary condition: server data shows member
		(
			(data.user?.id && data.user.member && !$isLoggingOut) ||
			// Fallback condition: client user data shows member (for cases where server data isn't available)
			(!data.user?.id && $user?.id && $user.member && !$isLoggingOut) ||
			// Additional fallback: profile store shows member (for cases where user store might be stale)
			($profileStore?.id && $profileStore.member && !$isLoggingOut)
		)
	);

	// // Track sidebar visibility changes for debugging
	// $: if (import.meta.env.DEV) {
	// 	console.log('🏗️ Layout: Sidebar visibility changed', {
	// 		showSidebar,
	// 		serverUserMember: data.user?.member,
	// 		clientUserMember: $user?.member,
	// 		profileStoreMember: $profileStore?.member,
	// 		isLoggingOut: $isLoggingOut
	// 	});
	// }

	// Only set profile from server data if we don't already have complete profile data
	// This prevents clearing the profile store during navigation
	if (data.profile && data.profile.id && data.profile.id !== '') {
		// Set profile from server data if we don't have a profile with the same ID
		const currentProfile = get(profileStore);
		if (!currentProfile?.id || currentProfile.id !== data.profile.id) {
			profileStore.set(data.profile);
		}
	}

	if (data.supabaseJWT) {
		setSupabaseAuthJWT(data.supabaseJWT, data.supabaseSession);
	}

	// when component loads
	onMount(async () => {
		// console.log('🏗️ Layout: Component mounted', {
		// 	serverUserMember: data.user?.member,
		// 	clientUserMember: $user?.member,
		// 	profileStoreMember: $profileStore?.member,
		// 	showSidebar
		// });

		hydrated.set(true);
		// Load Navbar component after initial render to avoid blocking LCP
		try {
			const module = await import('$lib/components/navbar/Navbar.svelte');
			NavbarComponent = module.default;
		} catch (error) {
			console.error('Failed to load Navbar component:', error);
		}

		// ParaModal is now loaded dynamically when needed, not here

		// Default to dark theme immediately
		if (browser) {
			const htmlElement = document.querySelector('html');
			if (htmlElement) {
				htmlElement.classList.add('dark');
			}
		}

		// Defer non-critical operations to avoid blocking LCP
		setTimeout(async () => {
			// Clean up any invalid JWTs before restoration
			if (browser) {
				const jwt = localStorage.getItem('supabase_jwt');
				if (jwt) {
					const payload = decodeJWTPayload(jwt);
					if (!payload || isJWTExpired(jwt)) {
						localStorage.removeItem('supabase_jwt');
						// Clear Supabase client authorization header (we don't use Supabase auth)
						(supabaseClient as any).rest.headers.Authorization = null;
					}
				}
			}

			// Restore JWT from localStorage if available
			if (browser) {
				await restoreSupabaseJWT();

				// Only restore user store from localStorage if server-side data is not available
				const jwt = localStorage.getItem('supabase_jwt');
				if (jwt && !$user) {
					try {
						// Safely verify and decode the JWT
						const payload = decodeJWTPayload(jwt);
						if (payload && payload.sub && !isJWTExpired(jwt)) {
							// Set basic user data from JWT without making database query
							user.set({
								id: payload.sub,
								walletAddress: '', // Will be updated from server-side data
								email: payload.email || '',
								member: false // Will be updated when profile loads
							});
						} else {
							// Token is invalid or expired, remove it
							localStorage.removeItem('supabase_jwt');
						}
					} catch {
						// Remove invalid token
						localStorage.removeItem('supabase_jwt');
					}
				}
			}

			// Load placenames with timeout protection (deferred)
			try {
				const placenamesPromise = loadPlacenames();
				const timeoutPromise = new Promise((_, reject) => {
					setTimeout(() => reject(new Error('Placenames query timeout')), 3000); // 3 second timeout
				});

				await Promise.race([placenamesPromise, timeoutPromise]);
			} catch (error) {
				if (import.meta.env.DEV) {
					console.warn('Failed to load placenames:', error);
				}
				// Continue without placenames data
			}
		}, 100); // Defer by 100ms to allow LCP to complete

		// Original dark/light theme switch:
		// if ($theme === 'dark') {
		// 	document.querySelector('html').classList.add('dark');
		// }
	});

	// Route history - for testing and troubleshooting, turn this on
	// beforeNavigate(({ to }) => {
	// 	$history = [to.pathname, ...$history];
	// 	// console.log('__layout.svelte::route', $history);
	// });

	$: if (
		$user?.id &&
		typeof $user.id === 'string' &&
		$user.id.length > 0 &&
		$profileStore.username &&
		(!$profileStore.avatar_url || $profileStore.avatar_url === 'avatar-default.svg')
	) {
		getProfile();
	}

	// Simplified profile loading logic - only trigger when absolutely necessary
	$: if (
		$user?.id &&
		typeof $user.id === 'string' &&
		$user.id.length > 0 &&
		(!$profileStore.id || $profileStore.id !== $user.id) &&
		!$profileLoading
	) {
		// Wrap in try-catch to prevent errors from causing 500 errors
		try {
			getProfile();
		} catch (error) {
			if (import.meta.env.DEV) {
				console.warn('Error triggering profile load:', error);
			}
			// Don't let profile loading errors crash the app
		}
	}

	// Only trigger avatar updates if we have a profile but missing avatar
	$: if (
		$user?.id &&
		$profileStore.id &&
		$profileStore.id === $user.id &&
		$profileStore.username &&
		(!$profileStore.avatar_url || $profileStore.avatar_url === 'avatar-default.svg') &&
		!$profileLoading
	) {
		// Wrap in try-catch to prevent errors from causing 500 errors
		try {
			getProfile();
		} catch (error) {
			if (import.meta.env.DEV) {
				console.warn('Error triggering avatar update:', error);
			}
			// Don't let profile loading errors crash the app
		}
	}

	// Update user store with member status from profile store
	$: if (
		$user?.id &&
		$profileStore.id &&
		$profileStore.id === $user.id &&
		$user.member !== $profileStore.member &&
		typeof $profileStore.member === 'boolean'
	) {
		user.update((currentUser) => {
			if (currentUser) {
				return {
					...currentUser,
					member: $profileStore.member || false
				};
			}
			return currentUser;
		});
	}

	// Force profile refresh if server data indicates member status but profile store doesn't match
	$: if (
		data.user?.id &&
		data.user.member &&
		$profileStore.id === data.user.id &&
		$profileStore.member !== data.user.member &&
		!$profileLoading
	) {
		try {
			getProfile();
		} catch (error) {
			if (import.meta.env.DEV) {
				console.warn('Error forcing profile refresh:', error);
			}
		}
	}

	// Debug sidebar visibility (simplified)
	// $: if (import.meta.env.DEV) {
	// 	console.log('Sidebar Debug:', {
	// 		showSidebar,
	// 		serverUser: data.user,
	// 		clientUser: $user,
	// 		profileStore: $profileStore,
	// 		isLoggingOut: $isLoggingOut
	// 	});
	// }
</script>

<svelte:head>
	<!-- Essential Meta Tags Only -->
	<meta name="viewport" content="width=device-width, initial-scale=1" />
	<meta name="robots" content="index, follow" />
	<meta name="theme-color" content="#1b0d2a" />

	<!-- Preconnect to external domains for performance -->
	<!-- Removed Google Fonts preconnect since we're self-hosting fonts -->

	<style>
		body {
			background-color: #1b0d2a; /* Example dark purple color */
		}
	</style>
</svelte:head>

{#if $navigating}
	<PageNavIndicator />
{/if}

<!-- ParaModal wrapper for dynamic loading - only loads heavy dependencies when user clicks login -->
<ParaModalWrapper />

<!-- Load Navbar directly without blocking the main content -->
<AlertList />
<div class="grid grid-flow-col bg-cosmicPurple800">
	{#if showSidebar}
		<div
			class="drawerContainer top-0 left-0 fixed z-10 h-full bg-cosmicPurple800 border-r border-cosmicPurplebg overflow-y-auto transition-opacity duration-300 {showSidebar
				? 'opacity-100'
				: 'opacity-0'}"
		>
			<Sidebar />
		</div>
	{/if}

	<!-- Main content area -->
	<main
		class="lg:flex-1 {showSidebar
			? 'lg:ml-48 2xl:ml-64 3xl:-pl-80'
			: ''} overflow-x-hidden min-h-screen flex flex-col"
	>
		<slot />
	</main>
</div>

<!-- Load Navbar after main content is rendered -->
{#if NavbarComponent}
	<svelte:component this={NavbarComponent} />
{/if}

<footer class={showSidebar ? 'lg:ml-48 4xl:-pl-96' : ''}>
	<ListSignup />

	<Footer />
</footer>
<!-- FAB: Only show for mobile, logged-in members -->
{#if ($user?.member === true || $profileStore.member) && $width < 640}
	<a
		rel="noreferrer"
		href="/create/creative/"
		class="fixed bottom-6 right-6 z-50 sm:hidden"
		aria-label="Create Post"
	>
		<button
			class="w-16 h-16 rounded-full flex items-center justify-center border-2 p-1 border-gradientBlueMid text-cosmicPurple800 shadow-lg shadow-black transition-transform duration-150 active:scale-95"
			aria-label="Create new post"
		>
			<svg
				class="w-10 h-10"
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 31.41 24.46"
				fill="none"
				aria-hidden="true"
			>
				<path
					d="M31.41 0C27.2 3.5 23.36 3.43 18.7 6.03s-4.87 6.54-4.87 6.54c-.15-1.11.13-3.7.13-3.7-1.46 1.91-1.56 3.71-1.37 6.77s-.24 5.81-.33 6.31.3.78.3.78c.12-.66.54-3.2.74-3.68 1.67-3.93 6.3-4.86 10.33-6.66 4.51-2.02 5.36-3.9 5.36-3.9a9.37 9.37 0 0 1-2.37.92C31.74 5.99 31.41 0 31.41 0ZM23.1 7.18c-4.04 1.9-7.44 3.71-9.53 10.18l-.65 1.98s-.18-.2.11-1.73c1.26-6.6 5.72-9.02 9.87-10.86C29.66 3.74 31.12.72 31.12.72s-.4 2.52-5.61 5.32c-.8.43-1.63.78-2.42 1.15Z"
					fill="#00d4ff"
				/>
			</svg>
		</button>
	</a>
{/if}

<style>
	/* Define scrollbar width and color */
	::-webkit-scrollbar {
		width: 12px;
	}

	/* Handle on hover and set background color */
	::-webkit-scrollbar-thumb {
		background-color: #392373; /* Adjust color as needed */
		border-radius: 6px;
	}

	/* Track background color */
	::-webkit-scrollbar-track {
		background-color: #261942; /* Adjust color as needed */
		border-radius: 6px;
	}

	/* Define scrollbar width and color for Firefox */
	/* Firefox supports scrollbar styling using the scrollbar-color property */
	/* You need to apply these styles to a specific container */
	/* In this example, we apply it to the body */
	.drawerContainer {
		scrollbar-width: thin;
		scrollbar-color: #261942 #392373; /* Adjust colors as needed */
		background-color: #1b0d2a;
	}
</style>
